import PropTypes from 'prop-types';
import { DataTable } from 'mantine-datatable';
import { useEffect, useState, useMemo } from 'react';
import Loading from '@/components/Loading';
import { filterData, paginateData, sortData } from '@/helpers/dataTable';
import ColumnFilter from '@/components/DataTable/ColumnFilter';
import styles from './style.module.css';

const CustomDataTable = ({
  loading, data, columns = [], filters, recordsPerPage = 20, setFilters, horizontalSpacing = 'xl', onVisibleRecordsChange,
}) => {
  const [sortStatus, setSortStatus] = useState({ columnAccessor: null, direction: 'asc' });
  const [page, setPage] = useState(1);

  const enhancedColumns = columns.map((column) => {
    if (column.isFilterActive) {
      const { accessor } = column;
      const filterComponent = (
        <ColumnFilter
          key={accessor}
          accessor={accessor}
          filters={filters}
          setFilters={setFilters}
          type={column.filterType || 'autoComplete'}
          options={column.filterOptions || []}
        />
      );
      const isFiltering = Array.isArray(filters[accessor])
        ? filters[accessor].some(Boolean)
        : (filters[accessor] ?? '').toString().length > 0;
      return {
        ...column,
        filter: filterComponent,
        filtering: isFiltering,
      };
    }
    return column;
  });

  const handleSortChange = (status) => {
    const { direction, columnAccessor } = status;
    let modifiedDirection = direction;
    if (sortStatus.columnAccessor !== columnAccessor) {
      modifiedDirection = 'asc';
    }
    const updatedSort = {
      columnAccessor,
      direction: modifiedDirection,
    };
    setSortStatus(updatedSort);
  };

  const sortedData = useMemo(
    () => sortData(data, sortStatus),
    [data, sortStatus],
  );

  const filteredData = useMemo(
    () => filterData(sortedData, filters, columns),
    [sortedData, filters],
  );

  const paginatedData = useMemo(
    () => paginateData(filteredData, page, recordsPerPage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredData, page],
  );

  useEffect(() => {
    onVisibleRecordsChange?.({
      visibleRecords: paginatedData,
      filteredRecords: filteredData,
      sortStatus,
      page,
      recordsPerPage,
    });
  }, [paginatedData, filteredData, sortStatus, page, recordsPerPage, onVisibleRecordsChange]);

  useEffect(() => {
    if (filteredData.length > 0 && paginatedData.length === 0) {
      setPage(1);
    }
  }, [filteredData, paginatedData]);
  if (loading) return <Loading />;

  return (
    <DataTable
      horizontalSpacing={horizontalSpacing}
      records={paginatedData}
      totalRecords={filteredData.length}
      recordsPerPage={recordsPerPage}
      page={page}
      onPageChange={setPage}
      sortStatus={sortStatus}
      onSortStatusChange={handleSortChange}
      idAccessor="_id"
      columns={enhancedColumns}
      classNames={{
        root: styles.root,
        table: styles.table,
      }}
      borderColor="#DCE0E5"
    />
  );
};

export default CustomDataTable;

CustomDataTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  recordsPerPage: PropTypes.number,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  horizontalSpacing: PropTypes.string,
  onVisibleRecordsChange: PropTypes.func,
};
